import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useI18n } from '../../i18n';
import { setPageMeta } from './pageMeta';

const steps = [
  ['how.steps.1.title', 'how.steps.1.copy', 'ph ph-images-square'],
  ['how.steps.2.title', 'how.steps.2.copy', 'ph ph-sparkle'],
  ['how.steps.3.title', 'how.steps.3.copy', 'ph ph-shopping-bag-open'],
];

const features = [
  ['how.feature.find.title', 'how.feature.find.copy', 'ph ph-crosshair'],
  ['how.feature.explain.title', 'how.feature.explain.copy', 'ph ph-list-checks'],
  ['how.feature.try.title', 'how.feature.try.copy', 'ph ph-person-simple-walk'],
  ['how.feature.save.title', 'how.feature.save.copy', 'ph ph-bookmark-simple'],
];

export default function HowItWorks() {
  const { requestLogin } = useAuth();
  const { t } = useI18n();

  useEffect(() => {
    setPageMeta({
      title: `${t('how.metaTitle')} | Outfit Detective`,
      description: t('how.metaDescription'),
      pathname: '/how-it-works',
      image: '/brand/outfit-detective-og.png',
    });
  }, [t]);

  return (
    <main className="ct-how-page">
      <section className="ct-how-hero">
        <div className="ct-how-hero-copy">
          <p className="ct-kicker">{t('how.kicker')}</p>
          <h1>{t('how.title')}</h1>
          <p>{t('how.copy')}</p>
          <div className="ct-how-actions">
            <button type="button" onClick={() => requestLogin({ title: t('authPrompt.title'), copy: t('authPrompt.copy') })}>
              <i className="ph ph-envelope-simple"></i>
              <span>{t('how.primaryCta')}</span>
            </button>
            <Link to="/fit-wall">
              <i className="ph ph-sparkle"></i>
              <span>{t('how.secondaryCta')}</span>
            </Link>
          </div>
        </div>

        <div className="ct-how-demo" aria-label={t('how.demoLabel')}>
          <div className="ct-how-phone">
            <div className="ct-how-phone-bar">
              <span></span>
              <strong>Outfit Detective</strong>
              <i className="ph ph-dots-three-outline"></i>
            </div>
            <div className="ct-how-post">
              <img src="/how-it-works-assets/screen-detect.svg" alt={t('how.demoImageAlt')} />
              <div className="ct-how-scan-box one"></div>
              <div className="ct-how-scan-box two"></div>
            </div>
            <div className="ct-how-result">
              <span>{t('how.demoDetected')}</span>
              <strong>{t('how.demoPieces')}</strong>
              <div className="ct-how-progress"><span></span></div>
            </div>
          </div>
          <div className="ct-how-product-card">
            <span>{t('how.demoMatch')}</span>
            <strong>{t('how.demoMatchTitle')}</strong>
            <em>{t('how.demoMatchMeta')}</em>
          </div>
        </div>
      </section>

      <section className="ct-how-strip" aria-label={t('how.promiseLabel')}>
        <div>
          <strong>{t('how.promise.1.value')}</strong>
          <span>{t('how.promise.1.label')}</span>
        </div>
        <div>
          <strong>{t('how.promise.2.value')}</strong>
          <span>{t('how.promise.2.label')}</span>
        </div>
        <div>
          <strong>{t('how.promise.3.value')}</strong>
          <span>{t('how.promise.3.label')}</span>
        </div>
      </section>

      <section className="ct-how-section">
        <div className="ct-how-section-head">
          <p className="ct-kicker">{t('how.stepsKicker')}</p>
          <h2>{t('how.stepsTitle')}</h2>
        </div>
        <div className="ct-how-steps">
          {steps.map(([titleKey, copyKey, icon], index) => (
            <article key={titleKey}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <i className={icon}></i>
              <h3>{t(titleKey)}</h3>
              <p>{t(copyKey)}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="ct-how-showcase">
        <div className="ct-how-showcase-media">
          <img src="/how-it-works-assets/screen-tryon.svg" alt="" />
          <div>
            <span>{t('how.showcaseBadge')}</span>
            <strong>{t('how.showcaseTitle')}</strong>
          </div>
        </div>
        <div className="ct-how-showcase-copy">
          <p className="ct-kicker">{t('how.featureKicker')}</p>
          <h2>{t('how.featureTitle')}</h2>
          <div className="ct-how-feature-list">
            {features.map(([titleKey, copyKey, icon]) => (
              <article key={titleKey}>
                <i className={icon}></i>
                <div>
                  <h3>{t(titleKey)}</h3>
                  <p>{t(copyKey)}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ct-how-trust">
        <div>
          <p className="ct-kicker">{t('how.trustKicker')}</p>
          <h2>{t('how.trustTitle')}</h2>
          <p>{t('how.trustCopy')}</p>
        </div>
        <ul>
          <li><i className="ph ph-check-circle"></i>{t('how.trust.1')}</li>
          <li><i className="ph ph-check-circle"></i>{t('how.trust.2')}</li>
          <li><i className="ph ph-check-circle"></i>{t('how.trust.3')}</li>
        </ul>
      </section>

      <section className="ct-how-final">
        <img src="/brand/outfit-detective-mark-transparent.svg" alt="" />
        <p className="ct-kicker">{t('how.finalKicker')}</p>
        <h2>{t('how.finalTitle')}</h2>
        <p>{t('how.finalCopy')}</p>
        <div className="ct-how-actions center">
          <button type="button" onClick={() => requestLogin({ title: t('authPrompt.title'), copy: t('authPrompt.copy') })}>
            <i className="ph ph-envelope-simple"></i>
            <span>{t('how.finalCta')}</span>
          </button>
          <Link to="/">{t('how.finalSecondary')}</Link>
        </div>
      </section>
    </main>
  );
}
